import { formControlClasses, inputClasses } from '@mui/material';
import { Stack } from '@mui/system';
import { memo, useCallback, useMemo } from 'react';
import {
  HighwayArea,
  ManualArea,
  RackingArea,
  SingularStationArea,
  StationaryArea,
} from '@thive/fddm-types';
import { useTranslation } from 'react-i18next';

import { SelectInput } from '@/modules/common/components/inputs/components/SelectInput';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import { Area } from '@/modules/floorplanDesign/types';
import { getVehicleTypesByArea } from '@/modules/floorplanDesign/helpers/vehicleType';
import { areaFactory } from '@/modules/floorplanDesign/factories';
import { assertNever } from '@/helpers/utils';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { SnackbarServiceFactory } from '@/modules/snackbar/service/SnackbarServiceFactory';


type VehicleTypeOption = {
  label: string;
  value: string;
};

const containerSx = {
  [`& .${formControlClasses.root} .${inputClasses.root}`]: {
    height: 'auto',
    padding: '4px 12px!important',
  },
};

const getSelectedArea = (selectedAreas: ReturnType<typeof useReadSelectedAreas>): Area | null => {
  const areas = areasToFlatCollection(selectedAreas);

  if (areas.length !== 1) {
    return null;
  }

  return areas[0];
};

const getSelectedVehicleTypeId = (area: Area | null): string => {
  if (!area) {
    return '';
  }

  const vehicleTypeIds = getVehicleTypesByArea(area);

  return vehicleTypeIds?.[0] ?? '';
};

const updateSingularVehicleType = (
  fddmSvc: IFddmService,
  area: SingularStationArea,
  vehicleTypeId: string,
): void => {
  fddmSvc.updateArea({
    ...area,
    loadHandlingPoiSpecs: [areaFactory.buildLoadHandlingPoiSpecBase(area.name, area.loadHandlingType, vehicleTypeId)],
  });
};

const updateStationaryVehicleType = (
  fddmSvc: IFddmService,
  area: StationaryArea,
  vehicleTypeId: string,
): void => {
  fddmSvc.updateArea({
    ...area,
    stationaryPoiSpecs: [areaFactory.buildStationaryPoiSpec(area.id, vehicleTypeId)],
  });
};

const updateRackingVehicleType = (
  fddmSvc: IFddmService,
  area: RackingArea,
  vehicleTypeId: string,
): void => {
  fddmSvc.updateArea({
    ...area,
    vehicleTypeId,
  });
};

const updateHighwayVehicleType = (
  fddmSvc: IFddmService,
  area: HighwayArea,
  vehicleTypeId: string,
): void => {
  fddmSvc.updateArea({
    ...area,
    highwayPoiSpecs: [areaFactory.buildHighwayAreaPoiSpec(area.id, vehicleTypeId)],
  });
};

const updateManualVehicleType = (
  fddmSvc: IFddmService,
  area: ManualArea,
  vehicleTypeId: string,
): void => {
  fddmSvc.updateArea({
    ...area,
    vehicleTypeIds: [vehicleTypeId],
  });
};

const updateAreaVehicleType = (fddmSvc: IFddmService, area: Area, vehicleTypeId: string): void => {
  fddmSvc.setPreference('area.vehicleTypeId', vehicleTypeId);

  switch (area.areaType) {
    case 'SINGULAR':
      updateSingularVehicleType(fddmSvc, area, vehicleTypeId);
      return;

    case 'STATIONARY':
      updateStationaryVehicleType(fddmSvc, area, vehicleTypeId);
      return;

    case 'RACKING':
      updateRackingVehicleType(fddmSvc, area, vehicleTypeId);
      return;

    case 'HIGHWAY':
      updateHighwayVehicleType(fddmSvc, area, vehicleTypeId);
      return;

    case 'MANUAL':
      updateManualVehicleType(fddmSvc, area, vehicleTypeId);
      return;

    default:
      assertNever(area);
  }
};

const VehicleTypeComponent = () => {
  const mode = useAppStore((state) => state.mode);
  const { allVehicles } = useVehicleStore();
  const selectedAreas = useReadSelectedAreas();
  const enabledVehicleTypeIds = useFloorplanDesignStore((state) => state.getVehicleTypeIds());
  const getRelatedFlows = useFloorplanDesignStore((state) => state.getRelatedFlows);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const selectedArea = useMemo(() => getSelectedArea(selectedAreas), [selectedAreas]);
  const snackbarSvc = useMemo(() => SnackbarServiceFactory.getInstance().createSnackbarService(), []);
  const { t } = useTranslation();

  const options = useMemo<VehicleTypeOption[]>(
    () =>
      allVehicles.getByIds(enabledVehicleTypeIds).map((vehicle) => ({
        label: vehicle.name,
        value: vehicle.id,
      })),
    [allVehicles, enabledVehicleTypeIds],
  );

  const selectedVehicleTypeId = useMemo(
    () => getSelectedVehicleTypeId(selectedArea),
    [selectedArea],
  );

  const isDisabled = mode !== 'EDITABLE' || !selectedArea;

  const handleChange = useCallback(
    (vehicleTypeId: string) => {
      if (isDisabled || !selectedArea) {
        return;
      }

      if (!vehicleTypeId || vehicleTypeId === selectedVehicleTypeId) {
        return;
      }

      if (getRelatedFlows([selectedArea.id]).length > 0) {
        snackbarSvc.showMessage(t('interface:properties.flows.change_vehicle_type_with_flow'));
        return;
      }

      updateAreaVehicleType(fddmSvc, selectedArea, vehicleTypeId);
    },
    [fddmSvc, getRelatedFlows, isDisabled, selectedArea, selectedVehicleTypeId, snackbarSvc, t],
  );

  return (
    <PropertiesLabel i18nkey='interface:properties.area.supported_vehicle.label'>
      <Stack sx={containerSx}>
        <SelectInput
          ellipsis
          disabled={isDisabled}
          options={options}
          onChange={handleChange}
          value={selectedVehicleTypeId}
        />
      </Stack>
    </PropertiesLabel>
  );
};

export const VehicleType = memo(VehicleTypeComponent);
